import type { ReactNode } from 'react';
import { styles } from '@/shared/app-stepper-form/styles';
import { StepRail } from '@/shared/app-stepper-form/parts/step-rail';
import type { StepState } from '@/shared/app-stepper-form/hooks/use-stepper';
import type { StepDefinition } from '@/shared/app-stepper-form/types';

/**
 * The scrollable contents of the step sheet: the active step's description, then
 * the full step list.
 *
 * Split out of `StepSheet` to keep that component inside the function-length
 * budget, and because the body is the part that carries the sheet's content while
 * `StepSheet` carries its behaviour — the open state, the jump-and-close rule and
 * the dialog composition.
 *
 * THE DESCRIPTION IS FOR THE ACTIVE STEP ONLY. Listing every step's description
 * would turn a short index into a wall of prose, and the user only needs to know
 * what the step they are on is for. It sits above the rail rather than inside the
 * active row so it reads as a caption for the sheet instead of as part of a list
 * item.
 *
 * The rail is the SAME component the desktop aside renders, so the status words,
 * the markers and the jump rules cannot drift apart between the two layouts. Only
 * the layout classes differ.
 */
export function StepSheetBody({
  step,
  steps,
  states,
  activeIndex,
  furthestReached,
  onJump,
}: {
  step: StepDefinition;
  steps: ReadonlyArray<StepDefinition>;
  states: ReadonlyArray<StepState>;
  activeIndex: number;
  furthestReached: number;
  onJump: (index: number) => void;
}): ReactNode {
  return (
    <div className={styles.sheetBody}>
      {step.description ? <p className={styles.sheetDescription}>{step.description}</p> : null}
      <StepRail
        steps={steps}
        states={states}
        activeIndex={activeIndex}
        furthestReached={furthestReached}
        onJump={onJump}
        className={styles.sheetRail}
      />
    </div>
  );
}
